import React, { ReactElement, useEffect, useState } from 'react';
import AppEmpty from '../../AppEmpty';
import styles from './HaProxyLbCompareDashboard.module.less';
import { Col, Row } from 'antd';
import useHaProxyStatsCompare from '../../../core/hooks/useHaProxyStatsCompare';
import HaProxyFrontendTrafficChart from '../../ccx/charts/HaProxyFrontendTrafficChart';
import HaProxyBackendTrafficChart from '../../ccx/charts/HaProxyBackendTrafficChart';

interface Props {
    uuid: string;
    from?: string;
    to?: string;
    interval: number;
    hidden?: boolean;
    displaySummary?: boolean;
    selectedNodes: any[];
}

function HaProxyLbCompareDashboard({
    uuid,
    from,
    to,
    interval,
    hidden,
    displaySummary,
    selectedNodes,
}: Props): ReactElement {
    const {
        stats: haProxyStats,
        refresh: haProxyStatsRefresh,
        loading: haProxyStatsLoading,
    } = useHaProxyStatsCompare(uuid, selectedNodes, from, to);
    const [sqlInterval, setSqlInterval] = useState<number | undefined>(
        undefined
    );
    const [refreshInterval, setRefreshInterval] = useState(interval);

    useEffect(() => {
        let interval: any;
        if (haProxyStats && refreshInterval > 0) {
            interval = setInterval(() => {
                haProxyStatsRefresh();
            }, refreshInterval);
            setSqlInterval(interval);
        }
        return () => clearInterval(interval);
    }, [haProxyStats, refreshInterval]);

    useEffect(() => {
        if (interval === 0) {
            // when interval is 0, we must pause the refresh (clear existing intervals)
            clearInterval(sqlInterval);
            setSqlInterval(0);
        }
        setRefreshInterval(interval);
    }, [interval]);

    useEffect(() => {
        if (hidden) {
            // clear interval to stop updating data if the tab is not active (charts are not visible)
            clearInterval(sqlInterval);
            setSqlInterval(0);
        } else if (sqlInterval === 0) {
            // force refresh if the interval is 0, otherwise, the interval will refresh data soon
            // interval is undefined on the first load
            // interval will be 0 when paused or tab is hidden
            haProxyStatsRefresh();
        }
    }, [hidden]);

    return (
        <section className={styles.HaProxyLbCompareDashboard}>
            {selectedNodes.length > 0 ? (
                <Row
                    gutter={[
                        { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 },
                        { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 },
                    ]}
                >
                    {selectedNodes.map((n: any, idx: number) => (
                        <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8} key={n}>
                            <h1>{n}</h1>

                            <Row
                                gutter={[
                                    {
                                        xs: 8,
                                        sm: 16,
                                        md: 24,
                                        lg: 32,
                                        xl: 40,
                                        xxl: 48,
                                    },
                                    {
                                        xs: 8,
                                        sm: 16,
                                        md: 24,
                                        lg: 32,
                                        xl: 40,
                                        xxl: 48,
                                    },
                                ]}
                                className={styles.HaProxyLbCompareDashboardRow}
                            >
                                <Col
                                    className={
                                        styles.HaProxyLbCompareDashboardCol
                                    }
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    lg={24}
                                    xl={24}
                                    xxl={24}
                                >
                                    <HaProxyFrontendTrafficChart
                                        data={
                                            haProxyStats
                                                ? haProxyStats[idx]
                                                : []
                                        }
                                        loading={haProxyStatsLoading}
                                        displaySummary={displaySummary}
                                    />
                                </Col>
                                <Col
                                    className={
                                        styles.HaProxyLbCompareDashboardCol
                                    }
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    lg={24}
                                    xl={24}
                                    xxl={24}
                                >
                                    <HaProxyBackendTrafficChart
                                        data={
                                            haProxyStats
                                                ? haProxyStats[idx]
                                                : []
                                        }
                                        loading={haProxyStatsLoading}
                                        displaySummary={displaySummary}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    ))}
                </Row>
            ) : (
                <AppEmpty message="No Load Balancer Nodes selected yet." />
            )}
        </section>
    );
}

export default HaProxyLbCompareDashboard;
